import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../lib/utils';

const vBadgeVariants = cva(
  'inline-flex items-center px-2.5 py-0.5 text-xs font-semibold',
  {
    variants: {
      color: {
        primary: 'bg-primary-100 text-primary-800',
        secondary: 'bg-secondary-100 text-secondary-800',
        success: 'bg-success-100 text-success-800',
        warning: 'bg-warning-100 text-warning-800',
        error: 'bg-error-100 text-error-800',
        info: 'bg-info-100 text-info-800',
        neutral: 'bg-neutral-200 text-neutral-700',
      },
      shape: {
        default: 'rounded-md',
        pill: 'rounded-full',
      },
    },
    defaultVariants: {
      color: 'neutral',
      shape: 'default',
    },
  },
);

type VBadgeProps = React.ComponentPropsWithoutRef<'span'> &
  VariantProps<typeof vBadgeVariants>;

/**
 * Status indicator / label. Use for all state pills, counts, and tags.
 * Pick `color` by semantic meaning (success, warning, etc.). Use `shape="pill"` for counts.
 */
export const VBadge = React.forwardRef<HTMLSpanElement, VBadgeProps>(
  function VBadge({ className, color, shape, children, ...props }, ref) {
    return (
      <span
        ref={ref}
        className={cn(vBadgeVariants({ color, shape, className }))}
        {...props}
      >
        {children}
      </span>
    );
  },
);
VBadge.displayName = 'VBadge';

export { vBadgeVariants };
export type { VBadgeProps };
